import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { 
  Search, 
  Clock, 
  FileText, 
  CheckCircle,
  Loader2,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const LTISelectExam = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const launchId = searchParams.get('launch_id');
  const user = getUser();
  
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExam, setSelectedExam] = useState(null);

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams');
      // Filter to only active exams (status can be 'active', 'published', or is_active=true)
      const activeExams = response.data.filter(e => 
        e.status === 'active' || e.status === 'published' || e.is_active === true
      );
      setExams(activeExams);
    } catch (err) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExam = async () => {
    if (!selectedExam) {
      toast.error('Please select an exam');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await api.post('/lti/deep-link/submit', {
        launch_id: launchId,
        exam_id: selectedExam.id,
        exam_title: selectedExam.title
      });

      // Create form and submit to Moodle
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = response.data.return_url;
      
      const jwtInput = document.createElement('input');
      jwtInput.type = 'hidden';
      jwtInput.name = 'JWT';
      jwtInput.value = response.data.jwt;
      form.appendChild(jwtInput);
      
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit selection');
      setSubmitting(false);
    }
  };

  const filteredExams = exams.filter(exam =>
    exam.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exam.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Select Exam to Embed</h1>
              <p className="text-sm text-slate-500">Choose an exam to add to your Moodle course</p>
            </div>
            <button
              onClick={handleSelectExam}
              disabled={!selectedExam || submitting}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Add to Course
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search exams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Exam List */}
        <div className="space-y-4">
          {filteredExams.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No published exams found</p>
              <p className="text-sm text-slate-400 mt-1">Create and publish exams in MyExamDesk first</p>
            </div>
          ) : (
            filteredExams.map(exam => (
              <div
                key={exam.id}
                onClick={() => setSelectedExam(exam)}
                className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all ${
                  selectedExam?.id === exam.id
                    ? 'border-indigo-500 ring-2 ring-indigo-100'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{exam.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{exam.subject}</p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {exam.duration || 60} mins
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {exam.questions?.length || 0} questions
                      </span>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedExam?.id === exam.id
                      ? 'border-indigo-500 bg-indigo-500'
                      : 'border-slate-300'
                  }`}>
                    {selectedExam?.id === exam.id && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LTISelectExam;
